/*
 * Copyright (c) 2025. Bolmate B.V.
 */

import React, {useContext, useEffect, useRef, useState} from 'react';

import {AppContext} from './appContext';
import {NotificationType, SubscriptionModuleName, SubscriptionModuleStatus} from '../../util/enums';
import Subscription from '../../models/subscriptionModel';
import {Loader, ResponseData} from '@dvrd/dvr-controls';
import {createErrorMessage} from '../../util/utils';
import {useLocation, useNavigate} from 'react-router';
import Notification, {defaultNotificationValues} from '../../models/notificationModel';
import {NOTIFICATIONS_ROUTE} from '../../util/constants';
import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

export default function URLActions() {
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {account} = context.accountContext;
    const {t} = useTranslation();
    const [loading, setLoading] = useState(false);
    const storedParams = useRef<URLSearchParams | null>(null);
    const location = useLocation();
    const navigate = useNavigate();

    function storeParams() {
        const params = storedParams.current ?? new URLSearchParams();
        const currentSearch = new URLSearchParams(location.search);
        for (const [key, value] of currentSearch.entries()) params.set(key, value);
        storedParams.current = params;
    }

    function processParams() {
        if (!user) return;
        let params: URLSearchParams;
        if (storedParams.current) params = storedParams.current;
        else params = new URLSearchParams(location.search);
        if (!params.size) return;
        for (const [key, value] of params.entries()) processParam(key, value);
        storedParams.current = null;
    }

    function processParam(key: string, value: string) {
        let remove = false;
        if (key === 'action') {
            if (value === 'add-module') {
                addNotificationModule();
                remove = true;
            } else if (value === 'add-order-notification') {
                addOrderNotification();
                remove = true;
            }
        }
        if (remove) {
            const params = new URLSearchParams(window.location.search);
            params.delete(key);
            history.replaceState({}, '', decodeURIComponent(`${window.location.pathname}?${params}`));
        }
    }

    function addNotificationModule() {
        const subscription = user?.subscription;
        if (
            !account ||
            !subscription ||
            subscription.hasActiveModule(SubscriptionModuleName.NOTIFICATIONS, [
                SubscriptionModuleStatus.CANCELING,
                SubscriptionModuleStatus.TRIAL
            ])
        )
            return;
        setLoading(true);
        subscription.addModule(
            {accountID: account.id, moduleName: SubscriptionModuleName.NOTIFICATIONS},
            (subscription: Subscription, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    showDialog(
                        t('misc.urlActions.moduleAdded', 'De Notificaties module is toegevoegd aan het abonnement.'),
                        t('misc.urlActions.moduleAddedTitle', 'Module toegevoegd')
                    );
                    user.subscription = subscription;
                    context.onChangeContext({user});
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'misc.urlActions.activateFailedMessage',
                                    'Het activeren van de Notificaties module is niet gelukt.'
                                )
                        ),
                        t('misc.urlActions.activateFailed', 'Activeren mislukt')
                    );
            }
        );
    }

    function addOrderNotification() {
        if (!account) return;
        setLoading(true);
        const notificationType = NotificationType.ORDER;
        const notification = new Notification({
            notification_type: notificationType,
            email_addresses: [account.email],
            account_id: account.id,
            ...defaultNotificationValues[notificationType]
        });
        notification.create((_: Notification, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                navigate(NOTIFICATIONS_ROUTE);
                showDialog(
                    t('misc.urlActions.notificationAdded', 'De order notificatie is toegevoegd.'),
                    t('misc.urlActions.notificationAddedTitle', 'Notificatie toegevoegd')
                );
            } else
                showDialog(
                    createErrorMessage(
                        data.message ??
                            t('misc.urlActions.addFailedMessage', 'Het toevoegen van de notificatie is niet gelukt.')
                    ),
                    t('misc.urlActions.addFailed', 'Toevoegen mislukt')
                );
        });
    }

    useEffect(() => {
        if (!user) storeParams();
    }, [user]);

    useEffect(() => {
        processParams();
    }, [location]);
    return <Loader active={loading} />;
}
